import axios from 'axios';

/**
 * Synchronous axios interceptor to rewrite the gamification SDK's
 * hardcoded default URL onto our same-origin proxy path.
 *
 * The SDK (`@stadionhq-v3/gamification-react`) sets
 * `axios.defaults.baseURL = 'https://public.development.internal.stadion.io/gamification'`
 * at module-load time, then `<GamificationProvider>` reconfigures axios
 * inside a `useEffect` to use our `apiBaseUrl`. But useEffect runs after
 * children mount, so the first poll fetch escapes to the internal URL —
 * which the browser can't resolve, and even when proxied the SDK has already
 * latched an error state by the time the URL is corrected.
 *
 * This file runs the interceptor as a side-effect on import (no `useEffect`,
 * no React lifecycle), so by the time any `GamificationProvider` child mounts
 * axios already routes those requests through `/api/gamification/*` — which
 * next.config.mjs rewrites server-side to the upstream platform.
 *
 * Imported once from the gamification provider so the side-effect fires
 * before any gamification component renders.
 */

const SDK_DEFAULT_HOST =
  'https://public.development.internal.stadion.io/gamification';

axios.interceptors.request.use((config) => {
  // Match either an explicit URL containing the SDK default, or a request
  // whose effective baseURL still points there.
  const effectiveBase = config.baseURL ?? '';
  const baseURL = effectiveBase.startsWith(SDK_DEFAULT_HOST)
    ? '/api/gamification'
    : config.baseURL;
  const url = config.url?.startsWith(SDK_DEFAULT_HOST)
    ? config.url.replace(SDK_DEFAULT_HOST, '/api/gamification')
    : config.url;
  return { ...config, baseURL, url };
});
